import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getListing, getBookedDates, createBooking, auth } from '../firebase';
import Calendar from '../components/Calendar';
import './ListingDetail.css';

function ListingDetail() {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [bookedDates, setBookedDates] = useState([]);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [bookingForm, setBookingForm] = useState({ guestName: '', guestEmail: '' });
  const [bookingLoading, setBookingLoading] = useState(false);
  const navigate = useNavigate();

  const fetchListing = useCallback(async () => {
    try {
      const data = await getListing(id);
      setListing(data);
      const booked = await getBookedDates(id);
      setBookedDates(booked);
    } catch (error) {
      alert('Failed to fetch listing');
      navigate('/');
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => {
    fetchListing();
  }, [fetchListing]);

  const handleSelectDates = (start, end) => {
    setCheckIn(start);
    setCheckOut(end);
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    
    if (!checkIn || !checkOut) {
      alert('Please select check-in and check-out dates');
      return;
    }

    if (!bookingForm.guestName || !bookingForm.guestEmail) {
      alert('Please enter your name and email');
      return;
    }

    setBookingLoading(true);
    try {
      const user = auth.currentUser;
      await createBooking(
        id,
        user?.uid || 'guest',
        checkIn.toISOString().split('T')[0],
        checkOut.toISOString().split('T')[0],
        bookingForm.guestName,
        bookingForm.guestEmail
      );
      alert('Booking request sent! The owner will contact you soon.');
      setCheckIn(null);
      setCheckOut(null);
      setBookingForm({ guestName: '', guestEmail: '' });
      await fetchListing();
    } catch (error) {
      alert('Failed to create booking: ' + error.message);
    } finally {
      setBookingLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!listing) return <div className="loading">Listing not found</div>;

  return (
    <div className="listing-detail-container">
      <button onClick={() => navigate('/')} className="back-btn">← Back</button>
      <div className="detail-content">
        <div className="photos-section">
          <div className="main-photo">
            <img src={listing.photos[currentPhotoIndex]} alt={listing.title} />
          </div>
          <div className="thumbnail-photos">
            {listing.photos.map((photo, index) => (
              <img key={index} src={photo} alt={`${listing.title} ${index + 1}`} className={index === currentPhotoIndex ? 'active' : ''} onClick={() => setCurrentPhotoIndex(index)} />
            ))}
          </div>
        </div>
        <div className="info-section">
          <h1>{listing.title}</h1>
          <p className="location">📍 {listing.location}</p>
          <div className="price-details">
            <div className="price">${listing.price}/month</div>
            <div className="specs">
              <span>🛏️ {listing.beds} Bedrooms</span>
              <span>🚿 {listing.baths} Bathrooms</span>
            </div>
          </div>
          <div className="description-section">
            <h2>About this listing</h2>
            <p>{listing.description}</p>
          </div>
          {listing.airbnbUrl && (
            <div className="airbnb-link">
              <h3>Check on Airbnb:</h3>
              <a href={listing.airbnbUrl} target="_blank" rel="noopener noreferrer">View on Airbnb →</a>
            </div>
          )}
          <div className="booking-section">
            <h3>📅 Check Availability & Book</h3>
            <Calendar 
              bookedDates={bookedDates}
              onSelectDates={handleSelectDates}
            />
            
            {checkIn && checkOut && (
              <form onSubmit={handleBooking} className="booking-form">
                <h4>Booking Details</h4>
                <input
                  type="text"
                  placeholder="Your Name"
                  value={bookingForm.guestName}
                  onChange={(e) => setBookingForm({...bookingForm, guestName: e.target.value})}
                  required
                />
                <input
                  type="email"
                  placeholder="Your Email"
                  value={bookingForm.guestEmail}
                  onChange={(e) => setBookingForm({...bookingForm, guestEmail: e.target.value})}
                  required
                />
                <div className="booking-summary">
                  <p><strong>Check-in:</strong> {checkIn.toLocaleDateString()}</p>
                  <p><strong>Check-out:</strong> {checkOut.toLocaleDateString()}</p>
                  <p><strong>Nights:</strong> {Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24))}</p>
                  <p className="total-price">
                    <strong>Total:</strong> ${listing.price * Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24))}
                  </p>
                </div>
                <button type="submit" className="book-btn" disabled={bookingLoading}>
                  {bookingLoading ? 'Sending Request...' : 'Request Booking'}
                </button>
              </form>
            )}
          </div>

          <div className="contact-section">
            <h3>Have Questions?</h3>
            <p>Contact the owner for more details.</p>
            <button className="contact-btn">Send Message</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ListingDetail;
